/**
 * AvvPage — Auftragsverarbeitungsvertrag (Art. 28 DSGVO) für Händler.
 * Zeigt AVV-Eckpunkte + Unterauftragsverarbeiter und ermöglicht versionierte Annahme.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileSignature, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function AvvPage() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/privacy/avv").then(({ data }) => setData(data)).catch(() => setData({ sub_processors: [] }));
    useEffect(() => { load(); }, []);

    const accept = async () => {
        setSaving(true);
        try {
            await api.post("/privacy/avv/accept");
            toast.success("AVV akzeptiert.");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bitte als Händler einloggen.");
        } finally { setSaving(false); }
    };

    if (!data) return null;

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="avv-page">
            <Link to={-1} onClick={(e) => { e.preventDefault(); window.history.back(); }} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#111827] mb-4">
                <ArrowLeft className="w-4 h-4" /> Zurück
            </Link>
            <div className="flex items-center gap-3 mb-2">
                <FileSignature className="w-7 h-7 text-[#FFCC00]" />
                <h1 className="font-display text-3xl font-black tracking-tight">Auftragsverarbeitungsvertrag (AVV)</h1>
            </div>
            <p className="text-sm text-gray-500 mb-6">Art. 28 DSGVO · Version {data.version}</p>

            <article className="prose prose-sm max-w-none space-y-4 text-[#111827]">
                <p>
                    Soweit Sie als Händler die Plattform nutzen, um personenbezogene Daten Ihrer
                    Kund:innen zu verarbeiten, sind Sie <strong>Verantwortlicher</strong> und die
                    TwoWheels (Inhaber Jörg Scholz) <strong>Auftragsverarbeiter</strong>. Dieser AVV regelt Gegenstand,
                    Dauer, Art und Zweck der Verarbeitung, die Kategorien betroffener Personen, die
                    technisch-organisatorischen Maßnahmen (Art. 32) sowie den Einsatz von
                    Unterauftragsverarbeitern.
                </p>
                <ul className="list-disc pl-6">
                    <li>Verarbeitung ausschließlich nach Weisung des Verantwortlichen.</li>
                    <li>Vertraulichkeitsverpflichtung aller Beschäftigten.</li>
                    <li>Unterstützung bei Betroffenenrechten und Meldepflichten (Art. 33/34).</li>
                    <li>Löschung/Rückgabe der Daten nach Vertragsende.</li>
                </ul>

                <h2 className="font-display text-xl font-bold">Unterauftragsverarbeiter</h2>
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase text-gray-400"><th className="py-1">Dienstleister</th><th>Ort</th><th>Zweck</th></tr></thead>
                    <tbody>
                        {(data.sub_processors || []).map((s, i) => (
                            <tr key={i} className="border-t border-gray-100">
                                <td className="py-1.5 font-medium">{s.name}</td>
                                <td className="text-gray-600">{s.location}</td>
                                <td className="text-gray-600">{s.purpose}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </article>

            <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
                {data.accepted ? (
                    <div className="flex items-center gap-2 text-emerald-700 font-bold" data-testid="avv-accepted">
                        <CheckCircle2 className="w-5 h-5" /> AVV akzeptiert {data.accepted_at ? `am ${new Date(data.accepted_at).toLocaleDateString("de-DE")}` : ""} (Version {data.accepted_version})
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-gray-600 mb-3">Mit Klick bestätigen Sie den AVV in Version {data.version} rechtsverbindlich (Nachweis mit Zeitstempel).</p>
                        <button onClick={accept} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-black disabled:opacity-60" data-testid="avv-accept-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} AVV akzeptieren
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
